import { useEffect, useState } from 'react';

export default function NeonReflections() {
  const [reflections, setReflections] = useState<Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    intensity: number;
    color: string;
    angle: number;
  }>>([]);
  const [globalIntensity, setGlobalIntensity] = useState(0.3);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    // Generate neon reflections
    const generateReflections = () => {
      const colors = [
        'rgba(0, 255, 255, ',
        'rgba(255, 0, 255, ',
        'rgba(255, 255, 0, ',
        'rgba(0, 255, 0, ',
        'rgba(255, 0, 0, '
      ];

      const newReflections = Array.from({ length: 12 }, (_, i) => ({
        id: `reflection-${i}`,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        width: 50 + Math.random() * 200,
        height: 20 + Math.random() * 100,
        intensity: 0.2 + Math.random() * 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        angle: Math.random() * 180 - 90
      }));
      setReflections(newReflections);
    };

    generateReflections();

    // Pulse effect
    const pulseInterval = setInterval(() => {
      setPulse(true);
      setGlobalIntensity(0.5 + Math.random() * 0.5);
      setTimeout(() => setPulse(false), 150);
    }, 2000 + Math.random() * 3000);

    // Random intensity changes
    const intensityInterval = setInterval(() => {
      setGlobalIntensity(0.2 + Math.random() * 0.4);
    }, 500);

    window.addEventListener('resize', generateReflections);

    return () => {
      clearInterval(pulseInterval);
      clearInterval(intensityInterval);
      window.removeEventListener('resize', generateReflections);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-20 overflow-hidden">
      {reflections.map((reflection) => (
        <div
          key={reflection.id}
          className={`absolute transition-all duration-300 ${
            pulse ? 'animate-electric-flicker' : ''
          }`}
          style={{
            left: reflection.x,
            top: reflection.y,
            width: reflection.width,
            height: reflection.height,
            transform: `rotate(${reflection.angle}deg) scale(${pulse ? 1.2 : 1})`,
            background: `linear-gradient(
              ${Math.random() * 360}deg,
              ${reflection.color}${(reflection.intensity * globalIntensity).toFixed(2)}) 0%,
              ${reflection.color}${(reflection.intensity * globalIntensity * 0.7).toFixed(2)}) 30%,
              transparent 70%
            )`,
            filter: `blur(${pulse ? 8 : 4}px)`,
            boxShadow: pulse ? `
              0 0 ${reflection.width * 0.5}px ${reflection.color}${(globalIntensity * 0.8).toFixed(2)}),
              0 0 ${reflection.width}px ${reflection.color}${(globalIntensity * 0.6).toFixed(2)}),
              0 0 ${reflection.width * 1.5}px ${reflection.color}${(globalIntensity * 0.4).toFixed(2)})
            ` : `
              0 0 ${reflection.width * 0.3}px ${reflection.color}${(globalIntensity * 0.6).toFixed(2)}),
              0 0 ${reflection.width * 0.6}px ${reflection.color}${(globalIntensity * 0.4).toFixed(2)})
            `
          }}
        />
      ))}
      
      {/* Edge glow effects */}
      <div 
        className={`absolute inset-0 ${pulse ? 'animate-pulse' : ''}`}
        style={{
          background: `
            linear-gradient(90deg, 
              rgba(0, 255, 255, ${globalIntensity * 0.1}) 0%, 
              transparent 10%, 
              transparent 90%, 
              rgba(255, 0, 255, ${globalIntensity * 0.1}) 100%
            ),
            linear-gradient(0deg, 
              rgba(255, 255, 0, ${globalIntensity * 0.1}) 0%, 
              transparent 10%, 
              transparent 90%, 
              rgba(0, 255, 0, ${globalIntensity * 0.1}) 100%
            )
          `,
          filter: `blur(${pulse ? 20 : 10}px)`
        }}
      />
      
      {/* Corner highlights */}
      {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner, index) => (
        <div
          key={corner}
          className={`absolute w-32 h-32 ${pulse ? 'animate-pulse' : ''}`}
          style={{
            [corner.includes('top') ? 'top' : 'bottom']: 0,
            [corner.includes('left') ? 'left' : 'right']: 0,
            background: `radial-gradient(
              circle at ${corner.includes('left') ? '0' : '100'}% ${corner.includes('top') ? '0' : '100'}%,
              rgba(${index % 2 === 0 ? '0, 255, 255' : '255, 0, 255'}, ${globalIntensity * 0.3}) 0%,
              transparent 70%
            )`,
            filter: `blur(${pulse ? 15 : 8}px)`
          }}
        />
      ))}
    </div>
  );
}